// Mock API service for Data Retention and Deletion Requests
// This provides a consistent interface that can be swapped with real API calls

export interface DeletionRequest {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  jurisdiction: 'US' | 'CA' | 'QC' | 'UK';
  requestDate: string;
  deadline: string;
  daysUntilDeadline: number;
  status: 'pending' | 'in_progress' | 'completed' | 'denied';
  priority: 'low' | 'medium' | 'high' | 'critical';
  reason: string;
  requestType: 'deletion' | 'export' | 'rectification' | 'access';
  assignedTo?: string;
  notes?: string;
  completedDate?: string;
  deniedReason?: string;
  // Additional fields for detail view
  requestNumber: string;
  requestTypeLabel: string;
  requestedBy: string;
  slaDeadline: string;
  estimatedCompletionDate?: string;
  daysRemaining: number; // Alias for daysUntilDeadline for consistency
  user: {
    id: string;
    name: string;
    email: string;
  };
  dataScope: {
    personalInfo: boolean;
    activityHistory: boolean;
    communications: boolean;
    billingData: boolean;
    analyticsData: boolean;
  };
  attachments?: Array<{
    id: string;
    name: string;
    size: string;
    uploadDate: string;
  }>;
  reviewedBy?: {
    name: string;
    email: string;
  };
  reviewDate?: string;
  reviewNotes?: string;
}

export interface DeletionRequestStats {
  pendingCount: number;
  inProgressCount: number;
  completedCount: number;
  overdueCount: number;
}

// Mock deletion requests data
const mockDeletionRequests: DeletionRequest[] = [
  {
    id: 'DR-2024-001',
    userId: 'user-123',
    userName: 'John Smith',
    userEmail: 'john.smith@email.com',
    jurisdiction: 'US',
    requestDate: '2024-01-05T10:30:00Z',
    deadline: '2024-02-05',
    daysUntilDeadline: 15,
    status: 'pending',
    priority: 'high',
    reason: 'I no longer use this service and want my personal data removed in accordance with CCPA regulations.',
    requestType: 'deletion',
    requestNumber: '001',
    requestTypeLabel: 'Full Data Deletion',
    requestedBy: 'Self-Service Portal',
    slaDeadline: '2024-02-05T23:59:59Z',
    daysRemaining: 15,
    user: {
      id: 'user-123',
      name: 'John Smith',
      email: 'john.smith@email.com'
    },
    dataScope: {
      personalInfo: true,
      activityHistory: true,
      communications: true,
      billingData: true,
      analyticsData: true
    },
    attachments: [
      {
        id: 'att-001-1',
        name: 'identity_verification.pdf',
        size: '2.4 MB',
        uploadDate: '2024-01-05T10:30:00Z'
      },
      {
        id: 'att-001-2',
        name: 'deletion_request_form.pdf',
        size: '856 KB',
        uploadDate: '2024-01-05T10:32:00Z'
      }
    ]
  },
  {
    id: 'DR-2024-002',
    userId: 'user-456',
    userName: 'Sarah Johnson',
    userEmail: 'sarah.j@email.com',
    jurisdiction: 'UK',
    requestDate: '2024-01-10T14:20:00Z',
    deadline: '2024-02-10',
    daysUntilDeadline: 20,
    status: 'in_progress',
    priority: 'medium',
    reason: 'Requesting complete deletion of my account and all associated data per GDPR Article 17.',
    requestType: 'deletion',
    assignedTo: 'Admin User',
    requestNumber: '002',
    requestTypeLabel: 'Full Data Deletion',
    requestedBy: 'Email Request',
    slaDeadline: '2024-02-10T23:59:59Z',
    estimatedCompletionDate: '2024-02-08',
    daysRemaining: 20,
    user: {
      id: 'user-456',
      name: 'Sarah Johnson',
      email: 'sarah.j@email.com'
    },
    dataScope: {
      personalInfo: true,
      activityHistory: true,
      communications: true,
      billingData: false,
      analyticsData: true
    },
    reviewedBy: {
      name: 'Admin User',
      email: 'admin@company.com'
    },
    reviewDate: '2024-01-11T09:15:00Z',
    reviewNotes: 'Verified identity. Approved for processing. Excluding billing data per retention policy.'
  },
  {
    id: 'DR-2024-003',
    userId: 'user-789',
    userName: 'Michael Brown',
    userEmail: 'mbrown@email.com',
    jurisdiction: 'CA',
    requestDate: '2023-12-20T16:45:00Z',
    deadline: '2024-01-20',
    daysUntilDeadline: -5,
    status: 'pending',
    priority: 'critical',
    reason: 'URGENT: Need immediate deletion due to privacy concerns under PIPEDA.',
    requestType: 'deletion',
    requestNumber: '003',
    requestTypeLabel: 'Emergency Data Deletion',
    requestedBy: 'Phone Call',
    slaDeadline: '2024-01-20T23:59:59Z',
    daysRemaining: -5,
    user: {
      id: 'user-789',
      name: 'Michael Brown',
      email: 'mbrown@email.com'
    },
    dataScope: {
      personalInfo: true,
      activityHistory: true,
      communications: true,
      billingData: true,
      analyticsData: true
    }
  },
  {
    id: 'DR-2024-004',
    userId: 'user-321',
    userName: 'Emily Davis',
    userEmail: 'emily.davis@email.com',
    jurisdiction: 'QC',
    requestDate: '2024-01-15T11:00:00Z',
    deadline: '2024-02-15',
    daysUntilDeadline: 25,
    status: 'pending',
    priority: 'medium',
    reason: 'Requesting data deletion in compliance with Quebec Law 25.',
    requestType: 'deletion',
    requestNumber: '004',
    requestTypeLabel: 'Full Data Deletion',
    requestedBy: 'Self-Service Portal',
    slaDeadline: '2024-02-15T23:59:59Z',
    daysRemaining: 25,
    user: {
      id: 'user-321',
      name: 'Emily Davis',
      email: 'emily.davis@email.com'
    },
    dataScope: {
      personalInfo: true,
      activityHistory: true,
      communications: false,
      billingData: true,
      analyticsData: false
    },
    attachments: [
      {
        id: 'att-004-1',
        name: 'quebec_resident_proof.pdf',
        size: '1.2 MB',
        uploadDate: '2024-01-15T11:00:00Z'
      }
    ]
  },
  {
    id: 'DR-2024-005',
    userId: 'user-654',
    userName: 'David Wilson',
    userEmail: 'dwilson@email.com',
    jurisdiction: 'US',
    requestDate: '2024-01-18T09:15:00Z',
    deadline: '2024-02-18',
    daysUntilDeadline: 3,
    status: 'pending',
    priority: 'high',
    reason: 'Request for deletion of all personal information under California Consumer Privacy Act.',
    requestType: 'deletion',
    requestNumber: '005',
    requestTypeLabel: 'CCPA Data Deletion',
    requestedBy: 'Self-Service Portal',
    slaDeadline: '2024-02-18T23:59:59Z',
    daysRemaining: 3,
    user: {
      id: 'user-654',
      name: 'David Wilson',
      email: 'dwilson@email.com'
    },
    dataScope: {
      personalInfo: true,
      activityHistory: true,
      communications: true,
      billingData: true,
      analyticsData: true
    }
  },
  {
    id: 'DR-2024-006',
    userId: 'user-987',
    userName: 'Jessica Martinez',
    userEmail: 'jmartinez@email.com',
    jurisdiction: 'UK',
    requestDate: '2023-12-01T13:30:00Z',
    deadline: '2024-01-01',
    daysUntilDeadline: -14,
    status: 'completed',
    priority: 'medium',
    reason: 'Account deletion request - no longer needed.',
    requestType: 'deletion',
    completedDate: '2024-01-02',
    notes: 'All data successfully purged. User notified via email.',
    requestNumber: '006',
    requestTypeLabel: 'Full Data Deletion',
    requestedBy: 'Email Request',
    slaDeadline: '2024-01-01T23:59:59Z',
    daysRemaining: -14,
    user: {
      id: 'user-987',
      name: 'Jessica Martinez',
      email: 'jmartinez@email.com'
    },
    dataScope: {
      personalInfo: true,
      activityHistory: true,
      communications: true,
      billingData: true,
      analyticsData: true
    },
    reviewedBy: {
      name: 'Compliance Officer',
      email: 'compliance@company.com'
    },
    reviewDate: '2023-12-02T10:00:00Z',
    reviewNotes: 'Standard deletion request. Identity verified via email confirmation link.'
  }
];

// Calculate stats from mock data
const calculateStats = (requests: DeletionRequest[]): DeletionRequestStats => {
  return {
    pendingCount: requests.filter(r => r.status === 'pending').length,
    inProgressCount: requests.filter(r => r.status === 'in_progress').length,
    completedCount: requests.filter(r => r.status === 'completed').length,
    overdueCount: requests.filter(r => r.daysUntilDeadline < 0 && r.status !== 'completed').length
  };
};

// API Functions
export const fetchDeletionRequests = async (): Promise<{ data: DeletionRequest[] }> => {
  console.log('📋 [Mock API] Fetching deletion requests...');

  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));

  return {
    data: mockDeletionRequests
  };
};

export const fetchDeletionRequestStats = async (): Promise<{ data: DeletionRequestStats }> => {
  console.log('📊 [Mock API] Fetching deletion request statistics...');

  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 300));

  const stats = calculateStats(mockDeletionRequests);

  return {
    data: stats
  };
};

export const fetchDeletionRequestDetails = async (requestId: string): Promise<{ data: DeletionRequest }> => {
  console.log(`📄 [Mock API] Fetching deletion request details for ${requestId}...`);

  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 400));

  const request = mockDeletionRequests.find(r => r.id === requestId);

  if (!request) {
    throw new Error(`Deletion request ${requestId} not found`);
  }

  return {
    data: request
  };
};

export const approveDeletionRequest = async (
  requestId: string,
  notes: string
): Promise<{ data: DeletionRequest }> => {
  console.log(`✅ [Mock API] Approving deletion request ${requestId}...`);
  console.log(`   Notes: ${notes}`);

  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1000));

  const request = mockDeletionRequests.find(r => r.id === requestId);

  if (!request) {
    throw new Error(`Deletion request ${requestId} not found`);
  }

  // Update request status in mock data
  request.status = 'in_progress';
  request.assignedTo = 'Current Admin User';
  request.notes = notes;

  return {
    data: { ...request }
  };
};

export const denyDeletionRequest = async (
  requestId: string,
  reason: string
): Promise<{ data: DeletionRequest }> => {
  console.log(`❌ [Mock API] Denying deletion request ${requestId}...`);
  console.log(`   Reason: ${reason}`);

  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1000));

  const request = mockDeletionRequests.find(r => r.id === requestId);

  if (!request) {
    throw new Error(`Deletion request ${requestId} not found`);
  }

  // Update request status in mock data
  request.status = 'denied';
  request.deniedReason = reason;

  return {
    data: { ...request }
  };
};

export const completeDeletionRequest = async (
  requestId: string,
  notes: string
): Promise<{ data: DeletionRequest }> => {
  console.log(`✔️ [Mock API] Completing deletion request ${requestId}...`);
  console.log(`   Notes: ${notes}`);

  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1500));

  const request = mockDeletionRequests.find(r => r.id === requestId);

  if (!request) {
    throw new Error(`Deletion request ${requestId} not found`);
  }

  // Update request status in mock data
  request.status = 'completed';
  request.completedDate = new Date().toISOString().split('T')[0];
  request.notes = notes;

  return {
    data: { ...request }
  };
};

// ============================================
// RETENTION STATISTICS & USER DATA API
// ============================================

// Extended retention status type to match component usage
export type RetentionStatus = 'active' | 'inactive' | 'scheduled' | 'purged' | 'approaching_purge' | 'scheduled_for_purge';
export type Jurisdiction = 'US' | 'CA' | 'QC' | 'UK';

export interface UserRetentionData {
  userId: string;
  lastActivityDate: Date;
  jurisdiction: Jurisdiction;
  retentionStatus: RetentionStatus;
  purgeScheduledDate?: Date;
  purgeWarningSentDate?: Date;
  daysUntilPurge?: number;
  warningLevel?: 90 | 30 | 7 | null;
}

export interface RetentionStatistics {
  totalUsers: number;
  activeUsers: number;
  approachingPurgeUsers: number;
  scheduledForPurgeUsers: number;
  byJurisdiction: {
    US: number;
    CA: number;
    UK: number;
  };
  byWarningLevel: {
    90: number;
    30: number;
    7: number;
  };
  pendingDeletionRequests: number;
  upcomingPurges: Array<{
    date: Date;
    count: number;
  }>;
}

// Mock user retention data
const mockUserRetentionData: UserRetentionData[] = [
  {
    userId: '1',
    lastActivityDate: new Date('2022-08-15'),
    jurisdiction: 'US',
    retentionStatus: 'scheduled_for_purge',
    purgeScheduledDate: new Date('2025-10-15'),
    purgeWarningSentDate: new Date('2025-09-15'),
    daysUntilPurge: 14,
    warningLevel: 7
  },
  {
    userId: '2',
    lastActivityDate: new Date('2022-09-20'),
    jurisdiction: 'CA',
    retentionStatus: 'approaching_purge',
    purgeScheduledDate: new Date('2025-10-30'),
    daysUntilPurge: 29,
    warningLevel: 30
  },
  {
    userId: '3',
    lastActivityDate: new Date('2022-07-10'),
    jurisdiction: 'UK',
    retentionStatus: 'scheduled_for_purge',
    purgeScheduledDate: new Date('2025-10-20'),
    purgeWarningSentDate: new Date('2025-07-20'),
    daysUntilPurge: 19,
    warningLevel: 7
  },
  {
    userId: '4',
    lastActivityDate: new Date('2024-01-15'),
    jurisdiction: 'US',
    retentionStatus: 'active',
    daysUntilPurge: 650
  }
];

// Mock retention statistics
const mockRetentionStatistics: RetentionStatistics = {
  totalUsers: 1247,
  activeUsers: 1089,
  approachingPurgeUsers: 142,
  scheduledForPurgeUsers: 16,
  byJurisdiction: {
    US: 687,
    CA: 342,
    UK: 218
  },
  byWarningLevel: {
    90: 89,
    30: 42,
    7: 11
  },
  pendingDeletionRequests: 8,
  upcomingPurges: [
    { date: new Date('2025-10-15'), count: 3 },
    { date: new Date('2025-10-22'), count: 5 },
    { date: new Date('2025-11-01'), count: 8 }
  ]
};

// Fetch retention statistics
export const fetchRetentionStatistics = async (): Promise<{ data: RetentionStatistics }> => {
  console.log('📊 [Mock API] Fetching retention statistics...');

  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));

  return {
    data: mockRetentionStatistics
  };
};

// Fetch user retention data
export const fetchUserRetentionData = async (): Promise<{ data: UserRetentionData[] }> => {
  console.log('👥 [Mock API] Fetching user retention data...');

  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 600));

  return {
    data: mockUserRetentionData
  };
};

// ============================================
// RETENTION SETTINGS API FUNCTIONS
// ============================================

// Settings interfaces
export interface RetentionPolicy {
  id: string;
  jurisdiction: string;
  jurisdictionCode: string;
  inactivityPeriodDays: number;
  gracePeriodDays: number;
  autoDelete: boolean;
  requiresApproval: boolean;
  notifyUserBeforeDays: number;
  enabled: boolean;
}

export interface SLASetting {
  id: string;
  requestType: string;
  requestTypeLabel: string;
  responseDays: number;
  completionDays: number;
  escalationDays: number;
  enabled: boolean;
}

export interface RetentionSettings {
  retentionPolicies: RetentionPolicy[];
  slaSettings: SLASetting[];
}

// Mock settings data
const mockRetentionSettings: RetentionSettings = {
  retentionPolicies: [
    {
      id: 'policy_001',
      jurisdiction: 'US (CCPA)',
      jurisdictionCode: 'ccpa',
      inactivityPeriodDays: 730, // 2 years
      gracePeriodDays: 30,
      autoDelete: true,
      requiresApproval: false,
      notifyUserBeforeDays: 60,
      enabled: true
    },
    {
      id: 'policy_002',
      jurisdiction: 'UK (GDPR)',
      jurisdictionCode: 'gdpr',
      inactivityPeriodDays: 1095, // 3 years
      gracePeriodDays: 90,
      autoDelete: false,
      requiresApproval: true,
      notifyUserBeforeDays: 90,
      enabled: true
    },
    {
      id: 'policy_003',
      jurisdiction: 'Canada (PIPEDA)',
      jurisdictionCode: 'pipeda',
      inactivityPeriodDays: 1825, // 5 years
      gracePeriodDays: 60,
      autoDelete: true,
      requiresApproval: false,
      notifyUserBeforeDays: 90,
      enabled: true
    },
    {
      id: 'policy_004',
      jurisdiction: 'Quebec (Law 25)',
      jurisdictionCode: 'quebec_law25',
      inactivityPeriodDays: 1095, // 3 years
      gracePeriodDays: 60,
      autoDelete: false,
      requiresApproval: true,
      notifyUserBeforeDays: 60,
      enabled: true
    }
  ],
  slaSettings: [
    {
      id: 'sla_001',
      requestType: 'gdpr_right_to_erasure',
      requestTypeLabel: 'GDPR Right to Erasure',
      responseDays: 30,
      completionDays: 30,
      escalationDays: 20,
      enabled: true
    },
    {
      id: 'sla_002',
      requestType: 'ccpa_deletion',
      requestTypeLabel: 'CCPA Data Deletion',
      responseDays: 45,
      completionDays: 45,
      escalationDays: 30,
      enabled: true
    },
    {
      id: 'sla_003',
      requestType: 'pipeda_deletion',
      requestTypeLabel: 'PIPEDA Data Deletion',
      responseDays: 30,
      completionDays: 30,
      escalationDays: 20,
      enabled: true
    },
    {
      id: 'sla_004',
      requestType: 'quebec_law25',
      requestTypeLabel: 'Quebec Law 25 Deletion',
      responseDays: 30,
      completionDays: 30,
      escalationDays: 20,
      enabled: true
    }
  ]
};

// Fetch retention settings
export const fetchRetentionSettings = async (): Promise<{ data: RetentionSettings }> => {
  console.log('⚙️ [Mock API] Fetching retention settings...');

  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));

  return {
    data: mockRetentionSettings
  };
};

// Update retention policy
export const updateRetentionPolicy = async (
  policyId: string,
  updates: Partial<RetentionPolicy>
): Promise<{ data: RetentionPolicy; message: string }> => {
  console.log(`💾 [Mock API] Updating retention policy ${policyId}...`);
  console.log('   Updates:', updates);

  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 800));

  const policy = mockRetentionSettings.retentionPolicies.find(p => p.id === policyId);

  if (!policy) {
    throw new Error(`Policy ${policyId} not found`);
  }

  // Update policy in mock data
  Object.assign(policy, updates);

  return {
    data: { ...policy },
    message: 'Retention policy updated successfully'
  };
};

// Update SLA settings
export const updateSLASettings = async (
  slaId: string,
  updates: Partial<SLASetting>
): Promise<{ data: SLASetting; message: string }> => {
  console.log(`💾 [Mock API] Updating SLA settings ${slaId}...`);
  console.log('   Updates:', updates);

  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 800));

  const sla = mockRetentionSettings.slaSettings.find(s => s.id === slaId);

  if (!sla) {
    throw new Error(`SLA ${slaId} not found`);
  }

  // Update SLA in mock data
  Object.assign(sla, updates);

  return {
    data: { ...sla },
    message: 'SLA settings updated successfully'
  };
};

// ============================================
// ADVANCED SETTINGS (DR-3B)
// ============================================

// Automation Settings
export interface AutomationSettings {
  id: string;
  autoDeletionEnabled: boolean;
  autoDeletionSchedule: 'daily' | 'weekly' | 'monthly';
  autoDeletionTime: string; // Format: "HH:mm"
  autoReminderEnabled: boolean;
  autoReminderDaysBefore: number;
  autoArchiveEnabled: boolean;
  autoArchiveDaysBefore: number;
  retryFailedDeletions: boolean;
  maxRetryAttempts: number;
  batchProcessingEnabled: boolean;
  batchSize: number;
  autoNotifyUsers: boolean;
  autoNotifyAdmins: boolean;
}

// Notification Settings
export interface NotificationSettings {
  id: string;
  adminNotifications: {
    newRequests: boolean;
    overdueRequests: boolean;
    completedRequests: boolean;
    failedDeletions: boolean;
  };
  userNotifications: {
    deletionWarning: boolean;
    deletionWarningDays: number;
    deletionConfirmation: boolean;
    deletionComplete: boolean;
    requestReceived: boolean;
    requestApproved: boolean;
    requestDenied: boolean;
  };
  notificationMethods: {
    email: boolean;
    sms: boolean;
    inApp: boolean;
  };
  escalationEnabled: boolean;
  escalationDays: number;
  escalationRecipients: string[]; // Email addresses
}

// Compliance Settings
export interface ComplianceSettings {
  id: string;
  auditLoggingEnabled: boolean;
  auditLogRetentionDays: number;
  verificationRequired: boolean;
  verificationMethods: string[]; // e.g., ['email', 'identity_document', '2fa']
  dataExportFormats: string[]; // e.g., ['json', 'csv', 'pdf']
  legalHoldEnabled: boolean;
  legalHoldBypassApproval: boolean;
  thirdPartyNotificationEnabled: boolean;
  thirdPartyProcessors: string[];
  complianceReportingEnabled: boolean;
  complianceReportingFrequency: 'weekly' | 'monthly' | 'quarterly';
  dataMinimizationEnabled: boolean;
  anonymizationBeforeDeletion: boolean;
}

// Combined advanced settings
export interface AdvancedRetentionSettings {
  automation: AutomationSettings;
  notifications: NotificationSettings;
  compliance: ComplianceSettings;
}

// Mock advanced settings data
const mockAdvancedSettings: AdvancedRetentionSettings = {
  automation: {
    id: 'auto_001',
    autoDeletionEnabled: true,
    autoDeletionSchedule: 'daily',
    autoDeletionTime: '02:00', // 2 AM
    autoReminderEnabled: true,
    autoReminderDaysBefore: 7,
    autoArchiveEnabled: true,
    autoArchiveDaysBefore: 30,
    retryFailedDeletions: true,
    maxRetryAttempts: 3,
    batchProcessingEnabled: true,
    batchSize: 50,
    autoNotifyUsers: true,
    autoNotifyAdmins: true
  },
  notifications: {
    id: 'notif_001',
    adminNotifications: {
      newRequests: true,
      overdueRequests: true,
      completedRequests: false,
      failedDeletions: true
    },
    userNotifications: {
      deletionWarning: true,
      deletionWarningDays: 7,
      deletionConfirmation: true,
      deletionComplete: true,
      requestReceived: true,
      requestApproved: true,
      requestDenied: true
    },
    notificationMethods: {
      email: true,
      sms: false,
      inApp: true
    },
    escalationEnabled: true,
    escalationDays: 5,
    escalationRecipients: ['compliance@company.com', 'legal@company.com']
  },
  compliance: {
    id: 'comp_001',
    auditLoggingEnabled: true,
    auditLogRetentionDays: 2555, // 7 years
    verificationRequired: true,
    verificationMethods: ['email', 'identity_document'],
    dataExportFormats: ['json', 'csv', 'pdf'],
    legalHoldEnabled: true,
    legalHoldBypassApproval: true,
    thirdPartyNotificationEnabled: true,
    thirdPartyProcessors: ['AWS', 'SendGrid', 'Stripe'],
    complianceReportingEnabled: true,
    complianceReportingFrequency: 'monthly',
    dataMinimizationEnabled: true,
    anonymizationBeforeDeletion: false
  }
};

// Fetch advanced settings
export const fetchAdvancedRetentionSettings = async (): Promise<{ data: AdvancedRetentionSettings }> => {
  console.log('⚙️ [Mock API] Fetching advanced retention settings...');

  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));

  return {
    data: mockAdvancedSettings
  };
};

// Update automation settings
export const updateAutomationSettings = async (
  updates: Partial<AutomationSettings>
): Promise<{ data: AutomationSettings; message: string }> => {
  console.log('🤖 [Mock API] Updating automation settings...');
  console.log('   Updates:', updates);

  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 800));

  // Update settings in mock data
  Object.assign(mockAdvancedSettings.automation, updates);

  return {
    data: { ...mockAdvancedSettings.automation },
    message: 'Automation settings updated successfully'
  };
};

// Update notification settings
export const updateNotificationSettings = async (
  updates: Partial<NotificationSettings>
): Promise<{ data: NotificationSettings; message: string }> => {
  console.log('🔔 [Mock API] Updating notification settings...');
  console.log('   Updates:', updates);

  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 800));

  // Deep merge for nested objects
  if (updates.adminNotifications) {
    Object.assign(mockAdvancedSettings.notifications.adminNotifications, updates.adminNotifications);
  }
  if (updates.userNotifications) {
    Object.assign(mockAdvancedSettings.notifications.userNotifications, updates.userNotifications);
  }
  if (updates.notificationMethods) {
    Object.assign(mockAdvancedSettings.notifications.notificationMethods, updates.notificationMethods);
  }

  // Update top-level properties
  Object.assign(mockAdvancedSettings.notifications, updates);

  return {
    data: { ...mockAdvancedSettings.notifications },
    message: 'Notification settings updated successfully'
  };
};

// Update compliance settings
export const updateComplianceSettings = async (
  updates: Partial<ComplianceSettings>
): Promise<{ data: ComplianceSettings; message: string }> => {
  console.log('📋 [Mock API] Updating compliance settings...');
  console.log('   Updates:', updates);

  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 800));

  // Update settings in mock data
  Object.assign(mockAdvancedSettings.compliance, updates);

  return {
    data: { ...mockAdvancedSettings.compliance },
    message: 'Compliance settings updated successfully'
  };
};

// ============================================
// OVERVIEW TAB API FUNCTIONS
// ============================================

export interface OverviewStats {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  scheduledUsers: number;
  purgedUsers: number;
  pendingRequests: number;
  avgResponseTime: number;
}

export interface OverviewUser {
  id: string;
  name: string;
  email: string;
  jurisdiction: string;
  lastActivity: string;
  status: 'Active' | 'Inactive' | 'Scheduled' | 'Purged';
  daysUntilPurge: number;
}

// Mock overview data
const mockOverviewStats: OverviewStats = {
  totalUsers: 40,
  activeUsers: 12,
  inactiveUsers: 12,
  scheduledUsers: 11,
  purgedUsers: 5,
  pendingRequests: 2,
  avgResponseTime: 59
};

const mockOverviewUsers: OverviewUser[] = [
  { id: '1', name: 'John Anderson', email: 'john.anderson@example.com', jurisdiction: 'US (CCPA)', lastActivity: '9/14/2025', status: 'Active', daysUntilPurge: 1063 },
  { id: '2', name: 'Sarah Mitchell', email: 'sarah.mitchell@example.com', jurisdiction: 'Canada (PIPEDA)', lastActivity: '9/19/2025', status: 'Active', daysUntilPurge: 703 },
  { id: '3', name: 'David Chen', email: 'david.chen@example.com', jurisdiction: 'UK (GDPR)', lastActivity: '9/24/2025', status: 'Active', daysUntilPurge: 708 },
  { id: '4', name: 'Maria Garcia', email: 'maria.garcia@example.com', jurisdiction: 'Quebec (Law 25)', lastActivity: '8/10/2025', status: 'Inactive', daysUntilPurge: 365 },
  { id: '5', name: 'James Wilson', email: 'james.wilson@example.com', jurisdiction: 'US (CCPA)', lastActivity: '7/22/2025', status: 'Active', daysUntilPurge: 912 },
  { id: '6', name: 'Emma Thompson', email: 'emma.t@example.com', jurisdiction: 'UK (GDPR)', lastActivity: '9/01/2025', status: 'Active', daysUntilPurge: 685 },
  { id: '7', name: 'Robert Lee', email: 'robert.lee@example.com', jurisdiction: 'US (CCPA)', lastActivity: '6/15/2025', status: 'Inactive', daysUntilPurge: 425 },
  { id: '8', name: 'Sophie Martin', email: 'sophie.m@example.com', jurisdiction: 'Canada (PIPEDA)', lastActivity: '9/20/2025', status: 'Active', daysUntilPurge: 752 },
  { id: '9', name: 'Michael Brown', email: 'michael.b@example.com', jurisdiction: 'US (CCPA)', lastActivity: '5/10/2025', status: 'Scheduled', daysUntilPurge: 180 },
  { id: '10', name: 'Lisa Wang', email: 'lisa.wang@example.com', jurisdiction: 'Quebec (Law 25)', lastActivity: '8/30/2025', status: 'Active', daysUntilPurge: 590 }
];

// Fetch overview statistics
export const fetchOverviewStats = async (): Promise<{ data: OverviewStats; _usingMockData?: boolean }> => {
  console.log('📊 [Mock API] Fetching overview statistics...');

  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));

  return {
    data: mockOverviewStats,
    _usingMockData: true
  };
};

// Fetch overview users with filters
export const fetchOverviewUsers = async (filters?: {
  search?: string;
  jurisdiction?: string;
  status?: string;
}): Promise<{ data: OverviewUser[]; _usingMockData?: boolean }> => {
  console.log('👥 [Mock API] Fetching overview users...', filters);

  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 600));

  let filteredUsers = [...mockOverviewUsers];

  // Apply filters
  if (filters?.search) {
    const query = filters.search.toLowerCase();
    filteredUsers = filteredUsers.filter(user =>
      user.name.toLowerCase().includes(query) ||
      user.email.toLowerCase().includes(query)
    );
  }

  if (filters?.jurisdiction && filters.jurisdiction !== 'all') {
    filteredUsers = filteredUsers.filter(user =>
      user.jurisdiction.includes(filters.jurisdiction!)
    );
  }

  if (filters?.status && filters.status !== 'all') {
    filteredUsers = filteredUsers.filter(user =>
      user.status === filters.status
    );
  }

  return {
    data: filteredUsers,
    _usingMockData: true
  };
};
